<template>
  <Modal
    v-model:isOpen="isOpenModel"
    modalId="edit-vessel-modal"
    :title="isUpdateMode ? 'Edit Vessel' : 'Add Vessel'"
    maxWidth="xl"
    @close="close"
  >
    <template #content="{ close: closeModal }">
      <div class="tw-space-y-4">
        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">Vessel Name *</label>
          <input
            v-model="form.name"
            type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
          />
        </div>

        <div>
          <label class="tw-block tw-text-sm tw-font-medium tw-text-slate-700">MMSI</label>
          <input
            v-model="form.mssi"
            type="text"
            class="tw-mt-1 tw-w-full tw-rounded-lg tw-border tw-border-slate-200/70 tw-bg-white tw-px-3 tw-py-2 tw-text-sm tw-text-slate-900 focus:tw-outline-none focus:tw-ring-2 focus:tw-ring-indigo-500/40"
          />
        </div>

        <div v-if="errorMessage" class="tw-text-sm tw-text-rose-700">
          {{ errorMessage }}
        </div>
      </div>

      <div class="tw-mt-5 tw-flex tw-justify-end tw-gap-2 tw-border-t tw-border-slate-200/70 tw-pt-4">
        <button
          type="button"
          class="tw-inline-flex tw-items-center tw-rounded-xl tw-border tw-border-slate-200 tw-bg-white tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-slate-700 hover:tw-bg-slate-50"
          @click="closeModal"
        >
          Cancel
        </button>
        <button
          type="button"
          class="tw-inline-flex tw-items-center tw-gap-2 tw-rounded-xl tw-border tw-border-transparent tw-bg-slate-900 tw-px-4 tw-py-2 tw-text-sm tw-font-semibold tw-text-white hover:tw-bg-slate-800 disabled:tw-opacity-60"
          :disabled="loading"
          @click="save"
        >
          <font-awesome-icon v-if="loading" icon="circle-notch" spin class="tw-h-4 tw-w-4" />
          <span>{{ isUpdateMode ? 'Save' : 'Add Vessel' }}</span>
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import { computed, reactive, watch } from 'vue';
import { useApolloClient } from '@vue/apollo-composable';

import Modal from '@/components/core/Modal/Modal.vue';
import CreateVesselMutation from '@/gql/orders/mutations/CreateVessel.gql';
import UpdateVesselMutation from '@/gql/orders/mutations/UpdateVessel.gql';
import VesselsQuery from '@/gql/orders/Vessels.gql';

const props = defineProps<{
  vessel?: { id?: number | string; name?: string; mssi?: string | null } | null;
}>();

const isOpenModel = defineModel<boolean>('isOpen', { default: false });

const emit = defineEmits<{
  (e: 'close'): void;
  (e: 'saved', vessel: any): void;
}>();

const apollo = useApolloClient();
const form = reactive({
  name: '',
  mssi: '',
});
const state = reactive({
  loading: false,
  errorMessage: '',
});

const isUpdateMode = computed(() => !!props.vessel?.id);

watch(
  () => [isOpenModel.value, props.vessel],
  () => {
    if (!isOpenModel.value) return;
    form.name = props.vessel?.name || '';
    form.mssi = props.vessel?.mssi || '';
    state.errorMessage = '';
  },
  { immediate: true, deep: true },
);

function close() {
  emit('close');
}

async function save() {
  const normalizedName = form.name.trim();
  if (!normalizedName) {
    state.errorMessage = 'Vessel name is required.';
    return;
  }

  state.loading = true;
  state.errorMessage = '';
  try {
    if (isUpdateMode.value) {
      const response = await apollo.client.mutate({
        mutation: UpdateVesselMutation,
        variables: {
          id: Number(props.vessel?.id),
          name: normalizedName,
          mssi: form.mssi.trim() || null,
        },
        update: (cache, { data }) => {
          // upon completion of the Update, update apollo cache
          const updated = data?.updateVessel?.vessel;
          if (!updated) return;

          // Invalidate paginated vessels cache (all variable variants), so the updated item will be found
          // not worth searching through each cache-pagination-search-query combination to update
          cache.evict({ fieldName: 'vesselsPaginated' });
          cache.evict({ fieldName: 'vesselsCount' });

          // Replace the updated vessel in the unpaginated `vessels` cache
          try {
            const existing: any = cache.readQuery({ query: VesselsQuery });
            if (existing?.vessels) {
              const nextVessels = existing.vessels.map((v: any) =>
                Number(v?.id) === Number(updated.id) ? { ...v, ...updated } : v,
              );
              cache.writeQuery({
                query: VesselsQuery,
                data: { vessels: nextVessels },
              });
            }
          } catch {
            // `vessels` query not in cache yet; nothing to update
          }

          cache.gc();
        },
      });
      emit('saved', response?.data?.updateVessel?.vessel);
    } else {
      const response = await apollo.client.mutate({
        mutation: CreateVesselMutation,
        variables: {
          name: normalizedName,
          mssi: form.mssi.trim() || null,
        },
        update: (cache, { data }) => {
          const created = data?.createVessel?.vessel;
          if (!created) return;

          // Invalidate paginated vessels cache (all variable variants), so the added (created) item will be found
          // not worth trying to add the item in the right place in each cache-pagination-search-query combination
          cache.evict({ fieldName: 'vesselsPaginated' });
          cache.evict({ fieldName: 'vesselsCount' });

          try {
            const existing: any = cache.readQuery({ query: VesselsQuery });

            // add to existing vessels cache if not already present
            if (existing?.vessels) {
              const alreadyPresent = existing.vessels.some(
                (v: any) => Number(v?.id) === Number(created.id),
              );
              if (!alreadyPresent) {
                cache.writeQuery({
                  query: VesselsQuery,
                  data: { vessels: [...existing.vessels, created] },
                });
              }
            } else {
            }
          } catch (e) {
            // `vessels` query not in cache yet; nothing to update
          }

          cache.gc();
        },
      });
      emit('saved', response?.data?.createVessel?.vessel);
    }
    isOpenModel.value = false;
  } catch (error: any) {
    state.errorMessage = error?.message || 'Failed to save vessel.';
  } finally {
    state.loading = false;
  }
}

const loading = computed(() => state.loading);
const errorMessage = computed(() => state.errorMessage);
</script>
